import styled, { css } from "styled-components";
import { InputHTMLAttributes } from "react";

import PhoneInput from "react-phone-input-2";

type InputProps = {
  isInvalid?: boolean;
  bordercolor?: string;
};

export const FormLabel = styled.label<InputProps & { $textColor?: string }>`
  font-family: "Montserrat", sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 1rem;
  color: ${(props) =>
    props.isInvalid ? "var(--red)" : props.$textColor || "var(--black)"};
  display: block;
  margin-bottom: 0.5rem;
  text-align: left;
`;

export const Input = styled.input<
  InputHTMLAttributes<HTMLInputElement> & { $bordercolor?: string }
>`
  font-family: "Montserrat", sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 1rem;
  line-height: 1.5rem;
  background: var(--gray-500);
  color: var(--black);
  padding: 0.5rem;
  border-radius: 0.125rem;
  border: 1px solid transparent;
  display: block;
  height: 3.125rem;
  width: 100%;

  &:focus {
    border-radius: 0.125rem;
    border: 2px solid ${(props) => props.$bordercolor || "var(--yellow-500)"};
    outline: none;
  }

  &::placeholder {
    font-size: 1rem;
    line-height: 1.5rem;
    color: #b6b6b6;
    font-weight: 800;
  }

  /* Autofill styles */
  &:-webkit-autofill {
    background: var(--gray-500) !important;
    color: var(--black) !important;
    -webkit-text-fill-color: var(--black) !important;
    transition: background-color 5000s ease-in-out 0s; /* Prevent flashing */
  }

  &:-webkit-autofill::first-line {
    font-family: "Montserrat", sans-serif;
    font-size: 1rem;
    font-weight: 500;
  }
`;

export const FormPhoneInput = styled(PhoneInput)<
  InputProps & { $bordercolor?: string; $textColor?: string }
>`
  .form-control {
    background: white;
    color: ${(props) =>
      props.isInvalid ? "var(--red)" : props.$textColor || "var(--black)"};
    padding: 0.5rem;
    border-radius: 0.125rem;
    border: 1px solid transparent;
    height: 3.125rem;
    width: 100%;
    padding-left: 4rem;
    font-family: "Montserrat", sans-serif;
    font-style: normal;
    font-weight: 500;
    font-size: 1rem;
    line-height: 1.5rem;
    text &:focus,
    &:focus-within {
      border-radius: 0.125rem;
      border: 2px solid
        ${(props) =>
          !props.isValid
            ? "var(--red)"
            : props.$bordercolor || "var(--yellow-500)"};
    }

    &::placeholder {
      font-size: 1rem;
      line-height: 1.5rem;
      color: #b6b6b6;
      font-weight: 800;
    }

    /* Autofill styles */
    &:-webkit-autofill {
      background: var(--gray-500) !important;
      color: var(--black) !important;
      -webkit-text-fill-color: var(--black) !important;
      transition: background-color 5000s ease-in-out 0s; /* Prevent flashing */
    }

    &:-webkit-autofill::first-line {
      font-family: "Montserrat", sans-serif;
      font-size: 1rem;
      font-weight: 500;
    }
  }

  &:focus-within {
    .form-control {
      border: 2px solid
        ${(props) =>
          props.isInvalid
            ? "var(--red)"
            : props.$bordercolor || "var(--yellow-500)"};
    }
  }

  .flag-dropdown {
    background: white;
    border: none;
    padding: 0.5rem;
    margin: 0.25rem;
    cursor: pointer;

    &:focus-within {
      outline: none;
    }
  }
`;

export const FormErrorMessage = styled.small`
  font-size: 0.75rem;
  line-height: 1.125rem;
  color: var(--red);
  margin-top: 0.25rem;
  display: block;
`;

export const FormControl = styled.div.withConfig({
  shouldForwardProp: (prop) => !["isInvalid", "$bordercolor"].includes(prop),
})<{ isInvalid?: boolean; $bordercolor?: string }>`
  ${FormLabel} {
    ${(props) =>
      props.isInvalid &&
      css`
        color: var(--red);
      `};
  }

  ${Input} {
    ${(props) =>
      props.isInvalid &&
      css`
        border: 1px solid var(--red);

        &:not(:focus)::placeholder {
          color: var(--red);
          font-weight: 600;
        }
      `};

    &:focus {
      ${(props) =>
        props.isInvalid &&
        css`
          border: 1px solid var(--red);
        `};
    }
  }

  ${FormPhoneInput} {
    ${(props) =>
      props.isInvalid &&
      css`
        border: 1px solid var(--red);

        &:not(:focus)::placeholder {
          color: var(--red);
          font-weight: 600;
        }
      `};

    &:focus {
      ${(props) =>
        props.isInvalid &&
        css`
          border: 1px solid var(--red);
        `};
    }
  }
`;
